import type { FlightOptionsContainer } from '@elara/client-core/containers/FlightOptionsContainer';
import type { FlightDirection, FlightOptionModel } from '@elara/client-core/models/FlightOptionModel';
import type { FlightSegment } from '@elara/domain/models/schemas/flight-schemas';

import { formatDuration } from '@elara/client-core/lib/date-utils';
import { cn, pluralize } from '@elara/client-core/lib/utils';

import { useApp, useItineraryState, useNavigate, useTripsState } from '#states/hooks.js';
import { Alert, AlertDescription } from '#ui/alert.js';
import { AirlineLogo, FlightActions } from '#ui/cards/FlightCard.js';
import { GoogleFlightBookingOptions } from '#ui/GoogleFlightBookingOptions.js';
import { Loader } from '#ui/loader.js';
import { Logo } from '#ui/logo.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { PriceStatusBanner } from '#ui/PriceStatusBanner.js';

import { GuideLayout } from './GuideLayout.js';
import { GuideNotFound } from './GuideNotFound.js';

const bulletSeparator = <div>∙</div>;

export function FlightSegmentHeader({ segment }: { segment: FlightSegment }) {
  return (
    <div className="col-start-1 col-span-2 row-start-1 row-span-1 flex items-center gap-1 text-sm pb-2 text-muted-foreground">
      <div className="w-4 @xl:w-6 flex items-baseline justify-center mr-1 @xl:mr-3">
        {segment.airline && <AirlineLogo airline={segment.airline} className="h-4 w-4" />}
      </div>
      <div className="whitespace-nowrap">{segment.airline.name}</div>
      {segment.flightNumber && (
        <>
          {bulletSeparator}
          <div className="whitespace-nowrap">{segment.flightNumber}</div>
        </>
      )}
      {segment.travelClass && (
        <>
          {bulletSeparator}
          <div className="whitespace-nowrap">{segment.travelClass}</div>
        </>
      )}
    </div>
  );
}

function FlightDetails({ flightOption, direction }: { flightOption: FlightOptionModel; direction: FlightDirection }) {
  const allFlights = flightOption.flightsForDirection(direction);

  return (
    <div className={cn('overflow-hidden transition-all duration-300 ease-in-out border p-4 rounded-md bg-background')}>
      <div className="space-y-4">
        {allFlights.map((flight, index) => {
          const isLastFlight = index === allFlights.length - 1;
          const layoverTime = flightOption.getLayoverTimeBetweenFlights(direction, index);

          return (
            <div key={index} className={cn('transition-all duration-300 ease-in-out')}>
              <div className="grid grid-cols-[16px_auto] grid-rows-[auto_auto_auto_auto_auto] @xl:grid-cols-[24px_1fr_1fr_1fr_auto] gap-y-1 gap-x-2 @xl:gap-x-4">
                {/* Vertical line with dots */}
                <div className="col-start-1 col-span-1 row-start-2 row-span-3 flex flex-col items-center py-0.5 @xl:py-1">
                  <div className="w-4 h-4 rounded-full border border-input"></div>
                  <div className="w-px flex-1 border-r border-input"></div>
                  <div className="w-4 h-4 rounded-full border border-input"></div>
                </div>

                {/* Flight number and airline */}
                <FlightSegmentHeader segment={flight} />

                {/* Origin */}
                <div className="col-start-2 col-span-1 row-start-2 row-span-1 text-sm @xl:col-span-2 @xl:text-base flex items-baseline gap-1 overflow-hidden">
                  <span className="whitespace-nowrap font-medium">{flight.formattedDepartureTime}</span>
                  {bulletSeparator}
                  <span className="truncate">
                    {flight.origin.airport_name} ({flight.originCode})
                  </span>
                </div>

                {/* Travel time */}
                <div className="col-start-2 col-span-1 row-start-3 row-span-1 text-sm my-3 @xl:col-span-2 text-muted-foreground">
                  {flight.formattedDuration ? `${flight.formattedDuration} travel time` : ''}
                </div>

                {/* Destination */}
                <div className="col-start-2 col-span-1 row-start-4 row-span-1 text-sm @xl:col-span-2 @xl:text-base flex items-baseline gap-1 overflow-hidden">
                  <span className="whitespace-nowrap font-medium">
                    {flight.formattedArrivalTime}{' '}
                    {flight.isOvernight && <sup className="text-xxs text-muted-foreground font-normal">+1</sup>}
                  </span>
                  {bulletSeparator}
                  <span className="truncate">
                    {flight.destination.airport_name} ({flight.destinationCode})
                  </span>
                </div>

                {/* Amenities */}
                {((flight.includes && flight.includes?.length > 0) || flight.aircraftType || flight.operatedBy) && (
                  <div className="col-start-2 col-span-1 row-start-5 row-span-1 text-2xs text-muted-foreground flex flex-col gap-2 mt-2 @xl:col-start-4 @xl:row-start-1 @xl:row-span-5 @xl:mt-3">
                    {flight.operatedBy && (
                      <div
                        key={`${flight.airline.icao_code}-${flight.flightNumber}-operated-by`}
                        className="flex items-center gap-2"
                      >
                        <MaterialIcon icon="airlines" size={IconSizes.xxs} className="text-muted-foreground" />
                        <div>Operated by {flight.operatedBy}</div>
                      </div>
                    )}
                    {flight.aircraftType && (
                      <div
                        key={`${flight.airline.icao_code}-${flight.flightNumber}-aircraft-type`}
                        className="flex items-center gap-2"
                      >
                        <MaterialIcon icon="flight" size={IconSizes.xxs} className="text-muted-foreground" />
                        <div>{flight.aircraftType}</div>
                      </div>
                    )}
                    {flight.includes?.map((amenity) => (
                      <div
                        key={`${flight.airline.icao_code}-${flight.flightNumber}-${amenity}`}
                        className="flex items-center gap-2"
                      >
                        <MaterialIcon
                          icon={flight.getAmenityIcon(amenity)}
                          size={IconSizes.xxs}
                          className="text-muted-foreground"
                        />
                        <div>{amenity}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Layover banner between flights */}
              {!isLastFlight && layoverTime > 0 && (
                <div className="my-5 -mx-4 p-4 text-sm flex items-center gap-2 text-muted-foreground bg-background-secondary">
                  <div className="w-4 @xl:w-6 flex items-center justify-center @xl:mr-2">
                    <MaterialIcon icon="airline_stops" size={IconSizes.xxs} className="text-muted-foreground" />
                  </div>
                  <span>
                    {layoverTime >= 60 ? `${formatDuration(layoverTime)} layover` : `${layoverTime}m layover`} at{' '}
                    {flight.destination.city_name ?? flight.destination.airport_name}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface FlightGuideProps {
  flightOptionId: string;
}

export function FlightGuide({ flightOptionId }: FlightGuideProps) {
  const tripsState = useTripsState();
  const itineraryState = useItineraryState();
  const { navigate } = useNavigate();
  const tripId = tripsState.current?.id;
  const { features } = useApp('features');
  const flightOptions: FlightOptionsContainer = (features as { flightOptions: FlightOptionsContainer }).flightOptions;

  if (!tripId) {
    return <GuideNotFound guide="flight" title="Flight not found" />;
  }

  const handle = flightOptions.ClientFlightOption.get({ tripId, id: flightOptionId });

  if (!handle.isReady) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader loading={true} />
      </div>
    );
  }

  if (!handle.data) {
    return <GuideNotFound guide="flight" title="Flight not found" />;
  }

  const flightOption = handle.data;

  const firstOutboundFlight = flightOption.firstFlight('outbound');
  const lastOutboundFlight = flightOption.lastFlight('outbound');
  const firstReturnFlight = flightOption.firstFlight('return');

  const originName = firstOutboundFlight?.origin.city_name ?? firstOutboundFlight?.origin.iata_code;
  const destinationName = lastOutboundFlight?.destination.city_name ?? lastOutboundFlight?.destination.iata_code;

  const title = flightOption.roundTrip ? `${originName} ↔ ${destinationName}` : `${originName} → ${destinationName}`;

  tripsState.setPreviewPanelTitle(title);

  const roundTripRequirementsMet =
    flightOption.roundTrip && flightOption.returnSegments && flightOption.returnSegments.length > 0;
  const oneWayRequirementsMet =
    !flightOption.roundTrip && flightOption.outboundSegments && flightOption.outboundSegments.length > 0;

  const hasBookingOptions = roundTripRequirementsMet || oneWayRequirementsMet;
  const bookedItem = itineraryState.items.find(
    (item) => item.type === 'flight' && item.referenceId === flightOption.id && item.isBooked,
  );
  const alreadyBooked = !!bookedItem;

  const actions = flightOption.hasActions ? <FlightActions flightOption={flightOption} /> : undefined;

  const metadata = (
    <>
      <div className="flex items-center gap-1">
        <MaterialIcon icon={flightOption.roundTrip ? 'connecting_airports' : 'flight_takeoff'} size={IconSizes.sm} />
        <span>{flightOption.roundTrip ? 'Round trip' : 'One-way'}</span>
      </div>

      {flightOption.formattedTravelClass('all') && (
        <div className="flex items-center gap-1">
          <MaterialIcon icon="flight_class" size={IconSizes.sm} />
          <span>{flightOption.formattedTravelClass('all')}</span>
        </div>
      )}

      {flightOption.totalPassengers && (
        <div className="flex items-center gap-1">
          <MaterialIcon icon="group" size={IconSizes.sm} />
          <span>
            {flightOption.totalPassengers} {pluralize(flightOption.totalPassengers, 'passenger')}
          </span>
        </div>
      )}
    </>
  );

  return (
    <GuideLayout title={title} actions={!flightOption.isAvailable ? undefined : actions} metadata={metadata}>
      {!alreadyBooked && (
        <PriceStatusBanner
          type="flight"
          model={flightOption}
          priceDivisor={flightOption.totalPaidPassengers}
          priceUnit="per passenger"
        />
      )}

      <div className="space-y-4">
        <h2 className="text-base @3xl:text-xl font-display flex items-center gap-2">
          <span>Outbound flight</span> {bulletSeparator} <span>{firstOutboundFlight?.longDepartureDate}</span>
        </h2>
        <FlightDetails flightOption={flightOption} direction="outbound" />
      </div>

      {flightOption.returnSegments && flightOption.returnSegments.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-base @3xl:text-xl font-display flex items-center gap-2">
            <span>Return flight</span> {bulletSeparator} <span>{firstReturnFlight?.longDepartureDate}</span>
          </h2>
          <FlightDetails flightOption={flightOption} direction="return" />
        </div>
      )}

      {flightOption.roundTrip && !hasBookingOptions && (
        <div className="space-y-4">
          <h2 className="text-base @3xl:text-xl font-display">Return flight</h2>
          <Alert className="flex items-center gap-2">
            <Logo variant="symbol" className="w-4 h-4" />
            <AlertDescription>Ask Elara for return flight options</AlertDescription>
          </Alert>
        </div>
      )}

      {hasBookingOptions && flightOption.bookingToken && !alreadyBooked && (
        <div className="space-y-3">
          <h2 className="text-base @3xl:text-xl font-display">Best booking option</h2>
          <GoogleFlightBookingOptions
            flightOption={flightOption}
            matchedOnly
            onBookOverride={() => {
              if (!tripId) return;
              navigate(`/checkout/flight/${tripId}/${flightOption.id}`);
            }}
          />
          <p className="text-xs text-muted-foreground">Powered by Google Flights.</p>
        </div>
      )}
    </GuideLayout>
  );
}
